import { useRef, useState, useEffect, useCallback } from 'react';
import { Undo2, Redo2, Trash2, Pen } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

interface Point {
  x: number;
  y: number;
  pressure: number;
}

type PenThickness = 'thin' | 'normal' | 'thick';
type PenColor = string;

const PEN_WIDTHS: Record<PenThickness, number> = {
  thin: 1.5,
  normal: 2.5,
  thick: 4,
};

const PEN_COLORS: { value: PenColor; labelKey: string }[] = [
  { value: '#1e293b', labelKey: 'veldwerk.handtekening.canvas.zwart' },
  { value: '#1e40af', labelKey: 'veldwerk.handtekening.canvas.blauw' },
];

interface SignatureCanvasProps {
  width?: number;
  height?: number;
  lineColor?: string;
  lineWidth?: number;
  onChange?: (hasContent: boolean) => void;
  className?: string;
  initialName?: string;
  showNameDate?: boolean;
}

export interface SignatureCanvasRef {
  toDataURL: (type?: string, quality?: number) => string;
  isEmpty: () => boolean;
  clear: () => void;
  getIpAddress: () => string | null;
  isMinimumSize: () => boolean;
  getName: () => string;
  getDate: () => string;
}

function formatDate(date: Date): string {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function SignatureCanvas({
  width = 600,
  height = 250,
  lineColor = '#1e293b',
  lineWidth = 2.5,
  onChange,
  className,
  initialName = '',
  showNameDate = true,
}: SignatureCanvasProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ w: width, h: height });

  // Pen settings
  const [activeThickness, setActiveThickness] = useState<PenThickness>('normal');
  const [activeColor, setActiveColor] = useState<PenColor>(lineColor);

  // Name and date
  const [name, setName] = useState(initialName);
  const [dateStr] = useState(() => formatDate(new Date()));

  // IP address
  const [ipAddress, setIpAddress] = useState<string | null>(null);

  // Total stroke length for minimum size validation
  const totalStrokeLength = useRef(0);

  // Undo/redo stacks — store ImageData snapshots
  const undoStack = useRef<ImageData[]>([]);
  const redoStack = useRef<ImageData[]>([]);
  const currentPath = useRef<Point[]>([]);

  // Fetch IP address on mount
  useEffect(() => {
    let cancelled = false;
    fetch('https://api.ipify.org?format=json')
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data?.ip) {
          setIpAddress(data.ip);
        }
      })
      .catch(() => {
        // Silently fail — IP logging is best-effort
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Responsive canvas sizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const { width: cw } = entries[0].contentRect;
      const w = Math.min(cw, width);
      const h = Math.round(w * (height / width));
      setCanvasSize({ w, h });
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [width, height]);

  // Set up canvas with correct DPI
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasSize.w * dpr;
    canvas.height = canvasSize.h * dpr;
    canvas.style.width = `${canvasSize.w}px`;
    canvas.style.height = `${canvasSize.h}px`;

    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = activeColor;

    // Redraw from undo stack if we have content
    if (undoStack.current.length > 0) {
      const last = undoStack.current[undoStack.current.length - 1];
      // Create a temporary canvas to restore
      const temp = document.createElement('canvas');
      temp.width = last.width;
      temp.height = last.height;
      temp.getContext('2d')!.putImageData(last, 0, 0);
      ctx.drawImage(temp, 0, 0, canvasSize.w, canvasSize.h);
    }
  }, [canvasSize, activeColor]);

  const getCtx = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d')!;
    ctx.strokeStyle = activeColor;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    return ctx;
  }, [activeColor]);

  const getPoint = useCallback((e: React.PointerEvent): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      pressure: e.pressure || 0.5,
    };
  }, []);

  const saveSnapshot = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    undoStack.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
    redoStack.current = []; // Clear redo on new action
  }, []);

  const updateHasContent = useCallback(
    (value: boolean) => {
      setHasContent(value);
      onChange?.(value);
    },
    [onChange],
  );

  // ─── Pointer handlers (unified touch/mouse/stylus) ───
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Capture pointer for smooth tracking
      canvas.setPointerCapture(e.pointerId);
      setIsDrawing(true);
      saveSnapshot();

      const point = getPoint(e);
      currentPath.current = [point];

      const ctx = getCtx();
      if (!ctx) return;
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    },
    [getPoint, getCtx, saveSnapshot],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDrawing) return;
      e.preventDefault();

      const point = getPoint(e);
      const prev = currentPath.current[currentPath.current.length - 1];
      currentPath.current.push(point);

      // Track stroke length
      if (prev) {
        const dx = point.x - prev.x;
        const dy = point.y - prev.y;
        totalStrokeLength.current += Math.sqrt(dx * dx + dy * dy);
      }

      const ctx = getCtx();
      if (!ctx) return;

      // Pressure-sensitive line width using active thickness
      const baseWidth = PEN_WIDTHS[activeThickness];
      const pressureWidth = baseWidth + point.pressure * baseWidth * 1.5;
      ctx.lineWidth = pressureWidth;
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    },
    [isDrawing, getPoint, getCtx, activeThickness],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isDrawing) return;
      setIsDrawing(false);
      currentPath.current = [];
      updateHasContent(true);

      const canvas = canvasRef.current;
      if (canvas) canvas.releasePointerCapture(e.pointerId);
    },
    [isDrawing, updateHasContent],
  );

  // ─── Actions ───
  const handleUndo = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || undoStack.current.length === 0) return;

    const ctx = canvas.getContext('2d')!;
    // Save current state for redo
    redoStack.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));

    const prev = undoStack.current.pop()!;
    ctx.putImageData(prev, 0, 0);

    if (undoStack.current.length === 0) {
      updateHasContent(false);
    }
  }, [updateHasContent]);

  const handleRedo = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || redoStack.current.length === 0) return;

    const ctx = canvas.getContext('2d')!;
    // Save current state for undo
    undoStack.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));

    const next = redoStack.current.pop()!;
    ctx.putImageData(next, 0, 0);
    updateHasContent(true);
  }, [updateHasContent]);

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d')!;
    // Save before clear for undo
    saveSnapshot();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    totalStrokeLength.current = 0;
    updateHasContent(false);
  }, [saveSnapshot, updateHasContent]);

  // Expose methods via ref-like pattern using data attributes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Attach methods to canvas element for parent access
    (canvas as any).__signatureMethods = {
      toDataURL: (type = 'image/png', quality = 1.0) => canvas.toDataURL(type, quality),
      isEmpty: () => !hasContent,
      clear: handleClear,
      getIpAddress: () => ipAddress,
      isMinimumSize: () => totalStrokeLength.current > 100,
      getName: () => name,
      getDate: () => dateStr,
    } satisfies SignatureCanvasRef;
  }, [hasContent, handleClear, ipAddress, name, dateStr]);

  return (
    <div className={clsx('flex flex-col gap-2', className)} ref={containerRef}>
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1">
          {/* Undo / Redo / Clear */}
          <button
            type="button"
            onClick={handleUndo}
            disabled={undoStack.current.length === 0}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 active:scale-95 transition-all"
            title={t('veldwerk.handtekening.canvas.ongedaan')}
          >
            <Undo2 size={18} />
          </button>
          <button
            type="button"
            onClick={handleRedo}
            disabled={redoStack.current.length === 0}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 active:scale-95 transition-all"
            title={t('veldwerk.handtekening.canvas.opnieuw')}
          >
            <Redo2 size={18} />
          </button>
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <button
            type="button"
            onClick={handleClear}
            disabled={!hasContent}
            className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 active:scale-95 transition-all"
            title={t('veldwerk.handtekening.canvas.wissen')}
          >
            <Trash2 size={18} />
          </button>

          {/* Pen thickness selector */}
          <div className="w-px h-5 bg-gray-200 mx-1" />
          {(Object.keys(PEN_WIDTHS) as PenThickness[]).map((thickness) => (
            <button
              key={thickness}
              type="button"
              onClick={() => setActiveThickness(thickness)}
              className={clsx(
                'flex items-center justify-center w-8 h-8 rounded-lg transition-all active:scale-95',
                activeThickness === thickness
                  ? 'bg-gray-200 text-gray-700'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
              )}
              title={t(`veldwerk.handtekening.canvas.${thickness === 'thin' ? 'dun' : thickness === 'normal' ? 'normaal' : 'dik'}`)}
            >
              <span
                className="rounded-full bg-current"
                style={{
                  width: PEN_WIDTHS[thickness] * 2.5,
                  height: PEN_WIDTHS[thickness] * 2.5,
                }}
              />
            </button>
          ))}

          {/* Color selector */}
          <div className="w-px h-5 bg-gray-200 mx-1" />
          {PEN_COLORS.map((color) => (
            <button
              key={color.value}
              type="button"
              onClick={() => setActiveColor(color.value)}
              className={clsx(
                'flex items-center justify-center w-8 h-8 rounded-lg transition-all active:scale-95',
                activeColor === color.value
                  ? 'ring-2 ring-offset-1 ring-gray-400'
                  : 'hover:bg-gray-100',
              )}
              title={t(color.labelKey)}
            >
              <span
                className="w-4 h-4 rounded-full border border-gray-300"
                style={{ backgroundColor: color.value }}
              />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Pen size={12} />
          {isDrawing
            ? t('veldwerk.handtekening.canvas.bezig')
            : hasContent
              ? t('veldwerk.handtekening.canvas.gezet')
              : t('veldwerk.handtekening.canvas.tekenHier')}
        </div>
      </div>

      {/* Canvas */}
      <div
        className={clsx(
          'relative rounded-xl border-2 border-dashed bg-white overflow-hidden cursor-crosshair transition-colors',
          isDrawing ? 'border-gold' : hasContent ? 'border-green-300' : 'border-gray-300',
        )}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          className="touch-none w-full"
          style={{ height: canvasSize.h }}
        />
        {/* Signature line */}
        <div className="absolute bottom-8 left-8 right-8 border-b border-gray-200 pointer-events-none" />
        <span className="absolute bottom-2 left-8 text-[10px] text-gray-300 pointer-events-none select-none">
          {t('veldwerk.handtekening.canvas.lijn')}
        </span>
      </div>

      {/* Name and date fields */}
      {showNameDate && (
        <div className="flex gap-3 mt-1">
          <div className="flex-1">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('veldwerk.handtekening.canvas.naam')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-colors"
            />
          </div>
          <div className="w-32">
            <input
              type="text"
              value={dateStr}
              readOnly
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 cursor-default"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Helper to get SignatureCanvasRef from a canvas element
export function getSignatureRef(canvasEl: HTMLCanvasElement | null): SignatureCanvasRef | null {
  if (!canvasEl) return null;
  return (canvasEl as any).__signatureMethods ?? null;
}
